import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Tag } from '../services/tagService';
import { useNavigate } from 'react-router-dom';
import { useLanguageContext } from '../contexts/LanguageContext';

// ✅ NEW: Build language-aware API URL
const buildLanguageAwareUrl = (baseUrl: string, languageCategoryId?: string): string => {
  if (!languageCategoryId) return baseUrl;
  
  const url = new URL(baseUrl, window.location.origin);
  url.searchParams.set('categories', languageCategoryId);
  
  return url.toString();
};

// Import TagBatchManager from SinglePostFeed for anti-spam optimization
class TagBatchManager {
  private static instance: TagBatchManager;
  private cache = new Map<number, any>();
  private pendingRequests = new Map<number, Promise<any>>();
  private batchQueue = new Set<number>();
  private batchTimeout: NodeJS.Timeout | null = null;
  private readonly BATCH_DELAY = 100; // ms
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes
  private languageCategoryId?: string; // ✅ NEW: Store language context

  static getInstance(): TagBatchManager {
    if (!TagBatchManager.instance) {
      TagBatchManager.instance = new TagBatchManager();
    }
    return TagBatchManager.instance;
  }

  // ✅ NEW: Set language context
  setLanguageContext(languageCategoryId?: string) {
    this.languageCategoryId = languageCategoryId;
  }

  async getTag(tagId: number): Promise<any> {
    // Check cache first
    const cached = this.cache.get(tagId);
    if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
      return cached.data;
    }

    // Check if request is already pending
    if (this.pendingRequests.has(tagId)) {
      return this.pendingRequests.get(tagId);
    }

    // Add to batch queue
    this.batchQueue.add(tagId);
    
    // Create promise for this tag
    const promise = new Promise<any>((resolve, reject) => {
      const checkForResult = () => {
        const cached = this.cache.get(tagId);
        if (cached && Date.now() - cached.timestamp < this.CACHE_TTL) {
          resolve(cached.data);
        } else {
          // Keep checking every 50ms for up to 5 seconds
          setTimeout(checkForResult, 50);
        }
      };
      
      // Start checking after batch delay
      setTimeout(checkForResult, this.BATCH_DELAY + 50);
      
      // Timeout after 5 seconds
      setTimeout(() => reject(new Error(`Tag ${tagId} fetch timeout`)), 5000);
    });

    this.pendingRequests.set(tagId, promise);
    
    // Schedule batch request
    this.scheduleBatchRequest();
    
    return promise;
  }

  private scheduleBatchRequest() {
    if (this.batchTimeout) {
      clearTimeout(this.batchTimeout);
    }

    this.batchTimeout = setTimeout(() => {
      this.executeBatchRequest();
    }, this.BATCH_DELAY);
  }

  private async executeBatchRequest() {
    if (this.batchQueue.size === 0) return;

    const tagIds = Array.from(this.batchQueue);
    this.batchQueue.clear();
    
    console.log(`[TagBatchManager] 🚀 Batch request for ${tagIds.length} tags:`, tagIds, {
      languageCategoryId: this.languageCategoryId
    });

    try {
      // ✅ FIXED: Try batch API first with language filtering
      if (tagIds.length > 1) {
        const baseUrl = `/api/tags?include=${tagIds.join(',')}&per_page=${tagIds.length}`;
        const batchResponse = await fetch(buildLanguageAwareUrl(baseUrl, this.languageCategoryId));
        if (batchResponse.ok) {
          const tags = await batchResponse.json();
          
          // Cache all results
          tags.forEach((tag: any) => {
            this.cache.set(tag.id, {
              data: tag,
              timestamp: Date.now()
            });
            this.pendingRequests.delete(tag.id);
          });
          
          // Handle missing tags
          tagIds.forEach(tagId => {
            if (!tags.find((t: any) => t.id === tagId)) {
              this.cache.set(tagId, {
                data: null,
                timestamp: Date.now()
              });
              this.pendingRequests.delete(tagId);
            }
          });
          
          console.log(`[TagBatchManager] ✅ Batch request completed, cached ${tags.length} tags (language filtered)`);
          return;
        }
      }

      // ✅ FIXED: Fallback to individual requests with language filtering
      const individualPromises = tagIds.map(async (tagId) => {
        try {
          const baseUrl = `/api/tags?include=${tagId}&per_page=1`;
          const response = await fetch(buildLanguageAwareUrl(baseUrl, this.languageCategoryId));
          if (response.ok) {
            const tagData = await response.json();
            const tag = tagData && tagData.length > 0 ? tagData[0] : null;
            
            this.cache.set(tagId, {
              data: tag,
              timestamp: Date.now()
            });
            
            return { tagId, tag };
          } else {
            this.cache.set(tagId, {
              data: null,
              timestamp: Date.now()
            });
            return { tagId, tag: null };
          }
        } catch (fetchError) {
          console.warn(`[TagBatchManager] Failed to fetch tag ${tagId}:`, fetchError);
          this.cache.set(tagId, {
            data: null,
            timestamp: Date.now()
          });
          return { tagId, tag: null };
        } finally {
          this.pendingRequests.delete(tagId);
        }
      });

      await Promise.all(individualPromises);
      console.log(`[TagBatchManager] ✅ Individual batch completed for ${tagIds.length} tags (language filtered)`);
      
    } catch (batchError) {
      console.error(`[TagBatchManager] ❌ Batch request failed:`, batchError);
      
      // Clear pending requests
      tagIds.forEach(tagId => {
        this.pendingRequests.delete(tagId);
      });
    }
  }

  // Get cache stats for debugging
  getCacheStats() {
    return {
      cacheSize: this.cache.size,
      pendingRequests: this.pendingRequests.size,
      batchQueueSize: this.batchQueue.size,
      languageCategoryId: this.languageCategoryId
    };
  }
}

interface HashtagSectionProps {
  tagIds?: number[]; // Optional for backward compatibility
  tagDetails?: { id: number; name: string; slug: string; count: number; }[]; // New prop for embedded tags
  className?: string;
  mode?: 'mobile-inline' | 'desktop-section'; // New prop for layout mode
}

// Mobile inline hashtags (TikTok style)
const InlineHashtagContainer = styled.span`
  display: inline;
  margin-left: 8px;
  
  @media (min-width: 769px) {
    display: none; // Hide on desktop when in mobile inline mode
  }
`;

const InlineHashtagItem = styled.button`
  background: none;
  border: none;
  color: inherit;
  font-size: inherit;
  font-weight: normal;
  cursor: pointer;
  padding: 0;
  margin: 0 4px 0 0;
  transition: all 0.2s ease;
  
  &:hover {
    color: inherit;
    opacity: 0.8;
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

// Desktop section hashtags
const DesktopHashtagContainer = styled.div`
  margin: 20px 0;
  padding: 20px;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.1);

  @media (max-width: 768px) {
    display: none; // Hide on mobile when in desktop mode
  }
`;

const DesktopHashtagTitle = styled.h3`
  color: #fff;
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 15px 0;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '#';
    color: #667eea;
    font-size: 20px;
    font-weight: bold;
  }
`;

const DesktopHashtagGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DesktopHashtagItem = styled.button<{ $isPopular?: boolean }>`
  background: ${props => props.$isPopular 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' 
    : 'rgba(255, 255, 255, 0.1)'};
  border: 1px solid ${props => props.$isPopular ? '#667eea' : 'rgba(255, 255, 255, 0.2)'};
  border-radius: 20px;
  padding: 8px 16px;
  color: #fff;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  white-space: nowrap;

  &::before {
    content: '#';
    color: ${props => props.$isPopular ? '#fff' : '#667eea'};
    font-weight: bold;
  }

  &:hover {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-color: #667eea;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.3);
  }

  &:active {
    transform: translateY(0);
  }
`;

// Loading states
const InlineLoadingSkeleton = styled.span`
  display: inline;
  margin-left: 8px;
  color: #4a9eff;
  opacity: 0.6;
`;

const DesktopLoadingSkeleton = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const DesktopSkeletonTag = styled.div`
  background: rgba(255, 255, 255, 0.1);
  border-radius: 20px;
  height: 36px;
  width: ${() => Math.random() * 60 + 80}px;
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0% { opacity: 0.6; }
    50% { opacity: 0.8; }
    100% { opacity: 0.6; }
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 14px;
  text-align: center;
  padding: 10px;
  background: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  border: 1px solid rgba(255, 107, 107, 0.2);

  @media (max-width: 768px) {
    font-size: 13px;
    padding: 8px;
  }
`;

const HashtagSection: React.FC<HashtagSectionProps> = ({ 
  tagIds, 
  tagDetails,
  className, 
  mode = 'desktop-section'
}) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { getCurrentLanguageCategoryId, isInitialized: isLanguageInitialized } = useLanguageContext();
  const tagBatchManager = TagBatchManager.getInstance();
  const languageCategoryId = getCurrentLanguageCategoryId();

  // ✅ NEW: Set language context in tag batch manager
  useEffect(() => {
    if (languageCategoryId) {
      tagBatchManager.setLanguageContext(languageCategoryId);
      console.log('[HashtagSection] 🌍 Language context set:', languageCategoryId);
    }
  }, [languageCategoryId, tagBatchManager]);

  useEffect(() => {
    // ✅ Wait for language detection before loading data
    if (!isLanguageInitialized) {
      console.log('[HashtagSection] ⏳ Waiting for language detection...');
      return;
    }

    // Priority 1: If tagDetails are provided, use them directly (OPTIMIZED PATH)
    if (tagDetails && tagDetails.length > 0) {
      console.log('[HashtagSection] 🚀 Using embedded tagDetails, no API call needed:', tagDetails.length, 'tags');
      setTags(tagDetails.map(tag => ({
        id: tag.id,
        name: tag.name,
        slug: tag.slug,
        count: tag.count,
        description: '',
        link: '',
        taxonomy: 'post_tag'
      })));
      setLoading(false);
      return;
    }

    // Priority 2: No tags - show empty state (no API call needed)
    if (!tagIds || tagIds.length === 0) {
      console.log('[HashtagSection] ℹ️ No tags available for this post');
      setLoading(false);
      return;
    }

    // Priority 3: Use TagBatchManager to prevent API spam (ANTI-SPAM SOLUTION)
    console.log('[HashtagSection] 🎯 Using TagBatchManager for', tagIds.length, 'tags (showing max 6)', {
      languageCategoryId
    });
    
    const fetchTagsWithBatchManager = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const limitedTagIds = tagIds.slice(0, 6); // Limit to 6 tags for UI space
        
        console.log('[HashtagSection] 🚀 Requesting tags via BatchManager:', limitedTagIds, {
          languageCategoryId
        });
        
        // Use batch manager to get all tags efficiently (no massive API calls!)
        const tagPromises = limitedTagIds.map(tagId => tagBatchManager.getTag(tagId));
        const fetchedTags = await Promise.all(tagPromises);
        
        // Filter out null/undefined results and convert to Tag format
        const validTags = fetchedTags
          .filter(tag => tag !== null && tag !== undefined)
          .map(tag => ({
            id: tag.id,
            name: tag.name,
            slug: tag.slug,
            count: tag.count,
            description: tag.description || '',
            link: '',
            taxonomy: 'post_tag'
          }));
        
        console.log('[HashtagSection] ✅ TagBatchManager completed:', validTags.length, 'out of', limitedTagIds.length, 'tags', {
          languageCategoryId
        });
        console.log('[HashtagSection] 📊 Cache stats:', tagBatchManager.getCacheStats());
        
        setTags(validTags);
      } catch (err: any) {
        console.error('[HashtagSection] ❌ Error fetching tags:', err);
        setError('Unable to load hashtags');
      } finally {
        setLoading(false);
      }
    };
    
    fetchTagsWithBatchManager();

  }, [tagIds, tagDetails, languageCategoryId, isLanguageInitialized, tagBatchManager]);

  if ((!tagIds || tagIds.length === 0) && (!tagDetails || tagDetails.length === 0)) {
    return null;
  }

  // Mobile inline mode (TikTok style)
  if (mode === 'mobile-inline') {
    if (loading) {
      return (
        <InlineLoadingSkeleton className={className}>
          #loading...
        </InlineLoadingSkeleton>
      );
    }

    if (error || tags.length === 0) {
      return null; // Don't show errors in inline mode
    }

    return (
      <InlineHashtagContainer className={className}>
        {tags.slice(0, 3).map((tag, index) => ( // Limit to 3 tags for mobile
          <InlineHashtagItem
            key={tag.id}
            onClick={() => navigate(`/tag/${tag.slug}`)}
            title={`View articles about ${tag.name}`}
          >
            #{tag.name}
          </InlineHashtagItem>
        ))}
      </InlineHashtagContainer>
    );
  }

  // Desktop section mode
  return (
    <DesktopHashtagContainer className={className}>
      <DesktopHashtagTitle>Hashtags</DesktopHashtagTitle>
      
      {loading && (
        <DesktopLoadingSkeleton>
          {Array.from({ length: Math.min((tagIds?.length || tagDetails?.length || 0), 6) }).map((_, index) => (
            <DesktopSkeletonTag key={index} />
          ))}
        </DesktopLoadingSkeleton>
      )}

      {error && (
        <ErrorMessage>{error}</ErrorMessage>
      )}

      {!loading && !error && tags.length > 0 && (
        <DesktopHashtagGrid>
          {tags.map((tag) => (
            <DesktopHashtagItem
              key={tag.id}
              $isPopular={!!(tag.count && tag.count > 50)}
              onClick={() => navigate(`/tag/${tag.slug}`)}
              title={`View all articles with hashtag ${tag.name}${tag.count ? ` (${tag.count} articles)` : ''}`}
            >
              {tag.name}
            </DesktopHashtagItem>
          ))}
        </DesktopHashtagGrid>
      )}

      {!loading && !error && tags.length === 0 && (
        <div style={{ color: '#aaa', fontSize: '14px', textAlign: 'center' }}>
          No hashtags for this article
        </div>
      )}
    </DesktopHashtagContainer>
  );
};

export default HashtagSection; 